import { useState, useEffect, useCallback, useRef } from 'react';
import { customerDuplicatesApi, EMPTY_GROUP_SUMMARY } from '../services/customerDuplicatesApi';
import type { GroupRow, GroupListSummary, GroupFilters, GroupSortField } from '../types/customerDuplicates';
import { EMPTY_GROUP_FILTERS } from '../types/customerDuplicates';

/**
 * State for the group-wide identity list.
 *
 * Same contract as `useCustomerDuplicatePairs`: sorting, filtering and paging are server-side and
 * every setter resets the page. The default sort is by WARNING, descending — the point of this
 * view is the customer one company has flagged and another has not, so it is what the first page
 * shows without anyone having to ask for it.
 *
 * Every filter defaults to empty, unlike the pair queue's `status=OPEN`. That is deliberate after
 * the P3 defect (handoff P3 § 4.2): a non-empty default on a dimension the tiles also COUNT is
 * exactly what made the "Decided" tile read zero forever. The one dimension here that is both a
 * tile and a filter — the warning — is counted server-side with its own filter removed.
 */

export const GROUP_PAGE_SIZE = 50;

export function useCustomerIdentityGroups(enabled: boolean) {
    const [rows, setRows] = useState<GroupRow[]>([]);
    const [summary, setSummary] = useState<GroupListSummary>(EMPTY_GROUP_SUMMARY);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<GroupFilters>(EMPTY_GROUP_FILTERS);
    const [sortBy, setSortBy] = useState<GroupSortField>('warning');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);

    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await customerDuplicatesApi.listGroups({
                page, limit: GROUP_PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setRows(result.items);
            setSummary(result.summary);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load customer identity groups');
            setRows([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [enabled, page, filters, sortBy, sortOrder]);

    useEffect(() => { void load(); }, [load]);

    const setFilters = useCallback((next: Partial<GroupFilters>) => {
        setFiltersState(prev => ({ ...prev, ...next }));
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setFiltersState(EMPTY_GROUP_FILTERS);
        setPage(1);
    }, []);

    const setSort = useCallback((field: GroupSortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('desc');
            return field;
        });
        setPage(1);
    }, []);

    const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== false).length;
    const totalPages = Math.max(1, Math.ceil(total / GROUP_PAGE_SIZE));

    return {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages,
        reload: load,
    };
}
